import { dateToLabel } from 'further-ui/utils';
import PageContainer from 'components/PageContainer';
import Table, { TableActions } from 'components/Table';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import AlertDialog from 'components/AlertDialog';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import api from 'lib/trpcClient';
import {
  CORE_FEE_TEMPLATE_ADD,
  createCoreFeeTemplateEditRoute,
} from 'adminConstants/routes';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useHistory } from 'react-router-dom';
import TextField from 'components/FormElements/AppTextInput';
import { FirmSelector } from 'components/FirmSelector';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { SortOrder } from 'further-types/utils';

type CoreFeeTemplate = {
  _id: string;
  name: string;
  createdAt?: string;
  firmName?: string;
  templateInUse?: boolean;
};

const pageId = 'core-fee-templates';

const CoreFeeTemplates: React.FC = () => {
  const deleteDisclosure = useDisclosure<{ templateId: string }>();
  const { isSuperAdmin } = useGetRole();
  const history = useHistory();
  const [canCreateCoreFeeTemplate, canDeleteCoreFeeTemplate] =
    useHasPermissions([
      RolePermission.CreateCoreFees,
      RolePermission.DeleteCoreFees,
    ]);

  const pagination = usePagination({
    orderBy: 'createdAt',
    order: SortOrder.Desc,
    id: pageId,
  });

  const defaultFilters = {
    searchByName: '',
  };

  const { filters, handleChangeFilters } = useFilters<{
    firmId?: string;
    searchByName: string;
  }>(pageId, {
    defaultFilters,
    onFiltersChange: pagination.toFirstPage,
  });

  const {
    data: coreFeeTemplates,
    isLoading: coreFeeTemplatesLoading,
    isFetching: coreFeeTemplatesFetching,
  } = api.coreFeeTemplates.list.useQuery({
    ...pagination.properties,
    firmId: filters.firmId,
    name: filters.searchByName,
  });

  const {
    mutate: deleteCoreFeeTemplate,
    isPending: deleteCoreFeeTemplatePending,
  } = api.coreFeeTemplates.remove.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Core fee template deleted successfully.',
      onSuccess: () => {
        pagination.toFirstPage();
        deleteDisclosure.onClose();
      },
    }),
  );

  const handleConfirmDelete = () => {
    deleteCoreFeeTemplate({
      id: deleteDisclosure.actionProps?.templateId,
    });
  };

  const columns = [
    ...(isSuperAdmin
      ? [
          {
            label: 'Firm',
            key: 'firm',
            render: (row: CoreFeeTemplate) => row.firmName,
          },
        ]
      : []),
    {
      label: 'Name',
      key: 'name',
    },
    {
      label: 'Date created',
      key: 'createdAt',
      render: (row: CoreFeeTemplate) => dateToLabel(row.createdAt),
    },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (row: CoreFeeTemplate) => (
        <TableActions
          showAsDropdown
          className="flex flex-row justify-center"
          actions={[
            {
              label: canCreateCoreFeeTemplate ? 'Edit' : 'View',
              icon: canCreateCoreFeeTemplate ? Edit : Visibility,
              color: 'primary',
              onClick: () => {
                history.push(createCoreFeeTemplateEditRoute(row._id));
              },
            },
            {
              label: 'Delete',
              icon: Delete,
              color: 'error',
              disabled: row.templateInUse,
              visible: canDeleteCoreFeeTemplate,
              onClick: () => {
                deleteDisclosure.stageAction({ templateId: row._id });
              },
              tooltip: row.templateInUse
                ? 'It is not possible to delete a fee preset that is currently in use.'
                : undefined,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageContainer heading="Fees: Core fee templates">
      <PageContent>
        <PageFilters
          pageId={pageId}
          buttons={[
            {
              link: CORE_FEE_TEMPLATE_ADD,
              visible: canCreateCoreFeeTemplate,
              label: 'Create new template',
            },
          ]}
        >
          {isSuperAdmin && (
            <Filter>
              <FirmSelector
                firmId={filters.firmId}
                placeholder="Search by firm"
                onChange={(firmId) => {
                  handleChangeFilters({ firmId });
                }}
              />
            </Filter>
          )}
          <Filter>
            <TextField
              type="search"
              name="searchByName"
              placeholder="Search by template name"
              fullWidth
              value={filters.searchByName}
              onChange={(event) => {
                handleChangeFilters({
                  searchByName: event.target.value,
                });
              }}
            />
          </Filter>
        </PageFilters>
        <Table
          loading={coreFeeTemplatesLoading || coreFeeTemplatesFetching}
          columns={columns}
          tablebody={coreFeeTemplates?.result ?? []}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          count={coreFeeTemplates?.total ?? 0}
          pagination={true}
          variant="nohover"
          emptyMessage={'No core fee templates found'}
        />

        <AlertDialog
          onClose={deleteDisclosure.onClose}
          onConfirm={handleConfirmDelete}
          open={deleteDisclosure.isOpen}
          title="Confirm delete"
          content={<p>Are you sure you wish to delete this template?</p>}
          confirmBtnProps={{
            loading: deleteCoreFeeTemplatePending,
          }}
          cancelBtnProps={{ variant: 'outlined', color: 'primary' }}
        />
      </PageContent>
    </PageContainer>
  );
};

export default CoreFeeTemplates;
